import { Ordering } from '@/types/graphql';
import { queuesForVariation } from '@axle-health/work-queue-definitions';

import { ContainerBulkAction } from '../ContainerVisitTable.types';
import {
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeOrdersRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import { definitionFor, fromDefinition } from '../fromDefinition';
import {
    EpisodeOfCareDashboardContainer,
    OrderDashboardContainer,
    PatientDashboardContainer,
    VisitDashboardContainer,
} from '../types';
import { HOSPICE_DEMO_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

const PUBLISHED_QUEUES = queuesForVariation('HOSPICE_DEMO');

const patientSchedulingLink = ({ id }: { id: string }): string => `/patients/${id}/scheduling`;

const ADMIT_VISIT_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.admissionsVisits),
    {
        defaultEnabled: true,
        hoverText: 'Admit visits that need scheduling.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No admit visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const RECENT_ADMITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.recentAdmits),
    {
        defaultEnabled: true,
        hoverText: 'Patients with a completed admit visit in the last 3 days.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No recent admits.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.incompleteCareTeam),
    {
        defaultEnabled: true,
        hoverText: 'Patients that need care teams assigned.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients with incomplete care teams.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const NEW_FREQUENCIES_CONTAINER: OrderDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.newFrequencies),
    {
        defaultEnabled: true,
        hoverText: 'New frequencies that need scheduling.',
        tableConfig: {
            rowToLink: defaultEpisodeOrdersRowToLink,
            emptyTableText: 'No new frequencies to schedule.',
            ordering: { fulfillmentWindowStartDate: Ordering.ASC },
        },
    },
);

const PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.patientsNeedingScheduling),
    {
        defaultEnabled: true,
        hoverText: 'Patients that need scheduling.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No patients to schedule.',
            ordering: { startDate: Ordering.ASC },
        },
    },
);

const UNASSIGNED_VISIT_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.unassigned),
    {
        defaultEnabled: true,
        hoverText: 'Visits in need of a clinician assignment.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No unassigned visits at this time',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const HUV_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.huv),
    {
        defaultEnabled: true,
        hoverText: 'Upcoming HUVs that need scheduling.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No HUVs to schedule.',
            ordering: { startDate: Ordering.ASC },
        },
    },
);

const SFV_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.sfv),
    {
        defaultEnabled: true,
        hoverText: 'Follow-up visits required within 2 days of Symptom Impact Assessments.',
        extraProps: { showClinicianColumn: false },
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No SFVs need attention at this time.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const EXPIRING_FULFILLMENT_WINDOW_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.expiringFulfillmentWindow),
    {
        defaultEnabled: true,
        hoverText: 'Visits with expiring fulfillment windows.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits with expiring fulfillment windows.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const DEATH_DISCHARGE_WITH_PENDING_VISITS_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.deathDischargeWithVisits),
    {
        defaultEnabled: true,
        hoverText: 'Visit cancellation required.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No deaths/discharges require visit cancellation at this time.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.inactiveClinicianVisits),
    {
        defaultEnabled: true,
        hoverText: 'Scheduled visits assigned to inactive clinicians',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No scheduled visits assigned to inactive clinicians',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const PATIENT_NO_SHOWS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.patientNoShows),
    {
        defaultEnabled: true,
        hoverText: 'Patient no-shows requiring attention',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No patient no-shows requiring attention.',
            ordering: { effectiveSortDate: Ordering.DESC },
        },
    },
);

const RECERT_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.recert),
    {
        defaultEnabled: true,
        hoverText: 'Upcoming Recerts that need scheduling.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No Recerts to schedule.',
            ordering: { endDate: Ordering.ASC },
        },
    },
);

const VISITS_WITHOUT_ACTIVE_SHIFTS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, HOSPICE_DEMO_CONTAINER_KEYS.visitsWithoutActiveShifts),
    {
        defaultEnabled: true,
        hoverText: 'Visits without active shifts',
        extraProps: {
            showClinicianColumn: true,
            bulkActions: [ContainerBulkAction.REASSIGN_VISITS],
        },
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits without shifts.',
            ordering: { effectiveDate: Ordering.ASC },
        },
    },
);

export const HOSPICE_DEMO_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        ADMIT_VISIT_CONTAINER,
        RECENT_ADMITS_CONTAINER,
        INCOMPLETE_CARE_TEAM_CONTAINER,
        NEW_FREQUENCIES_CONTAINER,
        PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        UNASSIGNED_VISIT_CONTAINER,
        HUV_CONTAINER,
        SFV_CONTAINER,
        EXPIRING_FULFILLMENT_WINDOW_CONTAINER,
        DEATH_DISCHARGE_WITH_PENDING_VISITS_CONTAINER,
        INACTIVE_CLINICIAN_VISITS_CONTAINER,
        PATIENT_NO_SHOWS_CONTAINER,
        RECERT_CONTAINER,
        VISITS_WITHOUT_ACTIVE_SHIFTS_CONTAINER,
    ],
};
